import React, { useEffect, useState } from "react";
import { IWorkTimeFormOptions } from "../../hooks/worktime/useWorkTimeFormOptions";
import MuiSelect, { IMuiSelectOption } from "../framework/muiSelect";
import { MuiDateTimePicker } from "../framework/muiDateTimePicker";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import { FormikProps, useFormik } from "formik";
import { defaultFormikConfig, formikFieldProps } from "../framework/formUtils";
import { IWorkTimeSaveData } from "../../models/workShiftTimeSlot/workTimeSaveData";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { Base } from "../../framework/common/base";
import { handleApiError } from "../../services/baseService";
import { useAppDispatch, useAppSelector } from "../../framework/redux/hooks";
import { addSuccessMsg } from "../../framework/redux/slices/appSlice";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { saveWorkShiftTimeSlot } from "../../framework/redux/slices/workShiftTimeSlotSlice";
import { ClearButton, SubmitButton } from "../framework/formButtons";
import { MainLayout, commonBoxStyle } from "../layout/mainLayout";
import { PageTitle } from "../layout/pageTitle";
import { optionOwnerHasHourbookingTypeSelectAllowed } from "../../models/common/consts";
import { useAppOptionValueEquals } from "../../hooks/framework/useAppOptionValue";

interface IWorkTimeFormProps {
    workShiftTimeSlotItem: IWorkTimeSaveData;
    formOptions: IWorkTimeFormOptions;
}

export const WorkTimeForm = ({
    workShiftTimeSlotItem,
    formOptions,
}: IWorkTimeFormProps) => {
    const {
        customers,
        allCostCenters, // All cost centers
        costCenters, // Has all cost centers or only employees favourites
        workTimeTypes,
        salaryRowTypes,
        vehicles,
        timeZones,
        myVehicles,
    } = formOptions;
    const [filteredCostCenters, setFilteredCostCenters] = useState<IMuiSelectOption[]>(costCenters);
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { id: selectedWorkTimeId, date } = useParams();
    const navigate = useNavigate();
    const [hourBookingTypeEnabled] = useAppOptionValueEquals(
        optionOwnerHasHourbookingTypeSelectAllowed,
        "1"
    );

    const isCommentRequired = useAppSelector(
        ({ session }) => session.employeeGroupParameters?.isCommentRequiredWhenEditingWorkTime
    ) && !!workShiftTimeSlotItem?.id;

    const handleSubmit = async (formData: IWorkTimeSaveData) => {
        await dispatch(saveWorkShiftTimeSlot(formData))
            .unwrap()
            .then(() => {
                dispatch(addSuccessMsg(t("workTime.workTimeSaveSuccessful")));
                goBack();
            })
            .catch(handleApiError);
    };
    const goBack = () => {
        navigate(generatePath("/worklog/:date?", { date }));
    };

    const formik: FormikProps<IWorkTimeSaveData> = useFormik({
        ...defaultFormikConfig,
        initialValues: workShiftTimeSlotItem,
        onSubmit: handleSubmit,
        validationSchema: yup.object({
            timeZoneName: yup.string().required(
                t("validation.fieldIsRequired", {
                    field: t("time.timeZone"),
                })
            ),
            startDate: yup.string().required(
                t("validation.fieldIsRequired", {
                    field: t("time.startTime"),
                })
            ),
            endDate: yup
                .string()
                .required(
                    t("validation.fieldIsRequired", {
                        field: t("time.endTime"),
                    })
                )
                .test(
                    "date-validation",
                    t("validation.endTimeMustBeAfterStartTime"),
                    function (value) {
                        const startDate: string = this.resolve(
                            yup.ref("startDate")
                        );
                        return Base.isSameOrAfter(value, startDate);
                    }
                ),
            salaryRowTypeId: yup
                .string()
                .nullable()
                .when("isCustomType", {
                    is: true,
                    then: (schema) =>
                        schema.required(
                            t("validation.fieldIsRequired", {
                                field: t("workTime.categoryCustomAddition"),
                            })
                        ),
                }),
            comment: isCommentRequired ? yup.string()
                .required(
                    t("validation.fieldIsRequired", {
                        field: t("common.comment"),
                    })
                )
                .matches(/^(?!\s+$).*/, t("validation.onlyWhitespacesNotAllowed", {
                    field: t("common.comment"),
                }))
                : null,
        }),
    });

    useEffect(() => {
        const customer = customers?.find((c) => c.id === formik.values.customerId);
        const costCenter = allCostCenters?.find(
            (c) => c.id === formik.values.costCenterId
        );

        if (customer && customer?.title !== costCenter?.group) {
            void formik.setFieldValue("costCenterId", null);
        }

        if (formik.values.customerId) {
            if (customer) {
                setFilteredCostCenters(
                    allCostCenters.filter(cc => cc.group.includes(customer.title))
                );
            }
        } else {
            setFilteredCostCenters(costCenters);
        }
    }, [formik.values.customerId, allCostCenters, customers]);

    useEffect(() => {
        if (formik.values.costCenterId) {
            const costCenter = allCostCenters.find(cc => cc.id === formik.values.costCenterId);
            const customer = customers.find(c => c.title === costCenter?.group);
            if (customer) {
                void formik.setFieldValue("customerId", customer.id);
            }
        }
    }, [formik.values.costCenterId]);

    useEffect(() => {
        if (
            !!formik.values.vehicleId &&
            !!myVehicles
        ) {
            const vehicle = myVehicles.find(
                (v) => v.id === formik.values.vehicleId
            );

            if (!!vehicle?.costCenterId) {
                void formik.setFieldValue("costCenterId", vehicle.costCenterId);
            }
        }
    }, [formik.values.vehicleId, myVehicles]);

    return (
        <MainLayout
            hasPx
            topComponent={
                <PageTitle
                    header={
                        selectedWorkTimeId
                            ? t("workTime.editWorkTimeEntry")
                            : t("workTime.newWorkTimeEntry")
                    }
                />
            }
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                }}
            >
                {customers?.length > 0 && (
                    <MuiSelect
                        label={t("workTime.customer")}
                        options={customers}
                        noSelection
                        {...formikFieldProps(formik, "customerId")}
                    />
                )}
                {allCostCenters?.length > 0 && (
                    <MuiSelect
                        label={t("workTime.costCenter")}
                        options={filteredCostCenters}
                        noSelection
                        {...formikFieldProps(formik, "costCenterId")}
                    />
                )}
                {salaryRowTypes.length !== 0 && hourBookingTypeEnabled && (
                    <MuiSelect
                        label={t("workTime.categoryCustomAddition")}
                        options={(salaryRowTypes || []).map((i) => ({
                            id: i.id,
                            title: i.name,
                        }))}
                        noSelection={!workShiftTimeSlotItem.isCustomType}
                        required={workShiftTimeSlotItem.isCustomType}
                        {...formikFieldProps(formik, "salaryRowTypeId")}
                    />
                )}
                {!workShiftTimeSlotItem.isCustomType &&
                    workTimeTypes?.length > 0 && (
                        <MuiSelect
                            label={t("workTime.workTimeType")}
                            options={(workTimeTypes || []).map((i) => ({
                                id: i.workTimeTypeId,
                                title: i.name,
                            }))}
                            noSelection
                            {...formikFieldProps(formik, "workTimeTypeId")}
                        />
                )}

                <MuiSelect
                    label={t("common.vehicle")}
                    options={vehicles}
                    noSelection
                    {...formikFieldProps(formik, "vehicleId")}
                />
                <MuiSelect
                    label={t("time.timeZone")}
                    options={timeZones}
                    required
                    {...formikFieldProps(formik, "timeZoneName")}
                />
                <Box sx={commonBoxStyle}>
                    <MuiDateTimePicker
                        dateLabel={t("time.startDate")}
                        timeLabel={t("time.startTime")}
                        {...formikFieldProps(formik, "startDate")}
                        onChange={(value) =>
                            void formik.setFieldValue("startDate", value)
                        }
                        required
                        asUtc={false}
                    />
                </Box>
                <Box sx={commonBoxStyle}>
                    <MuiDateTimePicker
                        dateLabel={t("time.endDate")}
                        timeLabel={t("time.endTime")}
                        {...formikFieldProps(formik, "endDate")}
                        onChange={(value) => {
                            void formik.setFieldValue("endDate", value);
                        }}
                        required
                        asUtc={false}
                    />
                </Box>
                <TextField
                    label={t("common.comment")}
                    multiline
                    minRows={2}
                    required={isCommentRequired}
                    fullWidth
                    {...formikFieldProps(formik, "comment")}
                />
                <Box sx={commonBoxStyle}>
                    <ClearButton formik={formik} />
                    <SubmitButton formik={formik} />
                </Box>
            </Box>
        </MainLayout>
    );
};
